import React from 'react';
import { FaChrome, FaDesktop, FaArrowRight, FaRedo, FaExclamationTriangle } from 'react-icons/fa';
import { SiBrave } from 'react-icons/si';
import { WebGpuIssue } from './webGpuProbe';
import './WebGpuCompatibility.css';

const DETAILS: Record<WebGpuIssue, string> = {
  'missing-api': 'This browser isn’t providing WebGPU, which the Virtual Hub needs to display the world.',
  'no-adapter': 'WebGPU is present in your browser, but a compatible graphics adapter isn’t available.',
  'probe-failed': 'We couldn’t complete the WebGPU check in this browser.',
  'timed-out': 'The graphics check took too long. Try checking again or restarting your browser.',
  'insecure': 'WebGPU requires a secure connection. Reopen the hub from the official Intersect website using HTTPS.',
};

interface Props {
  issue: WebGpuIssue;
  onRetry: () => void;
  onContinue: () => void;
  mobile?: boolean;
  streamLabel?: string;
}

export default function WebGpuCompatibility({ issue, onRetry, onContinue, mobile = false, streamLabel = 'Continue in stream view' }: Props) {
  return <main className="gpu-check">
    <header className="gpu-check-header"><span className="gpu-check-brand">Intersect<span aria-hidden="true">✳</span></span><span>VIRTUAL HUB</span></header>
    <section className="gpu-check-card" aria-labelledby="gpu-check-title">
      <div className="gpu-check-eyebrow"><FaDesktop aria-hidden="true" /> BROWSER COMPATIBILITY</div>
      <div className="gpu-check-status"><FaExclamationTriangle aria-hidden="true" /> WebGPU unavailable</div>
      <h1 id="gpu-check-title">Let’s get you into<br className="gpu-check-desktop-break" /> the Virtual Hub.</h1>
      <p className="gpu-check-intro">{DETAILS[issue]}<br />A browser update or a change to your graphics settings may help.</p>
      <h2>Try an up-to-date browser</h2>
      <div className="gpu-check-browsers">
        <a className="gpu-check-browser" href="https://www.google.com/chrome/" target="_blank" rel="noopener noreferrer">
          <FaChrome className="gpu-check-browser-icon" aria-hidden="true" /><span><strong>Google Chrome</strong><small>A good place to start on desktop</small></span><FaArrowRight aria-hidden="true" />
        </a>
        <a className="gpu-check-browser" href="https://brave.com/download/" target="_blank" rel="noopener noreferrer">
          <SiBrave className="gpu-check-browser-icon" aria-hidden="true" /><span><strong>Brave</strong><small>Another option on desktop</small></span><FaArrowRight aria-hidden="true" />
        </a>
      </div>
      <p className="gpu-check-launch-note">Open the Intersect website in your chosen browser and launch the hub again. Browser links open in a new tab.</p>
      <div className="gpu-check-help">
        <strong>Already using one of these?</strong>
        <p>Update your browser, turn on graphics acceleration in its settings, then restart it. If the check still fails, try another computer or ask your IT team for help.</p>
        {mobile && <p>On iPhone or iPad, update iOS or iPadOS and try Safari. If the Virtual Hub still won’t load, use a desktop computer.</p>}
        <p className="gpu-check-note">WebGPU availability depends on your browser, operating system and graphics hardware.</p>
      </div>
      <div className="gpu-check-actions"><button className="gpu-check-primary" onClick={onRetry}><FaRedo aria-hidden="true" /> Check again</button><button className="gpu-check-secondary" onClick={onContinue}>{streamLabel} <FaArrowRight aria-hidden="true" /></button></div>
      <p className="gpu-check-footnote">You can still watch the event without entering the 3D world.</p>
    </section>
    <footer className="gpu-check-footer">INTERSECT VIRTUAL HUB <span>•</span> A place to connect</footer>
  </main>;
}
